/**
 * Run the shared form engine over each built instance to detect blocking errors
 * and soft warnings — the SAME engine the wizard/preview use, so import
 * validation never diverges from interactive data entry.
 *
 * Transform-level cell errors (bad number/date) are folded in as blocking: a
 * value that couldn't be parsed can't be committed.
 */
import { runFormEngine } from '@/lib/form-engine'
import type { BlockingError, SoftWarning } from '@/lib/form-engine'
import type {
  FieldDefinition,
  ConditionalRule,
} from '@/features/form-builder/api/form-builder-types'
import type { BuiltInstance, BuiltRecord } from './build-records'

export interface InstanceValidation {
  /** field_key → blocking error (engine rules + required + transform failures). */
  blockingErrors: Record<string, BlockingError>
  softWarnings: SoftWarning[]
  hasBlocking: boolean
  hasWarnings: boolean
}

export function validateInstance(
  fields: FieldDefinition[],
  conditionalRules: ConditionalRule[],
  inst: BuiltInstance,
): InstanceValidation {
  const snap = runFormEngine(fields, conditionalRules, inst.fieldValues)
  const blockingErrors: Record<string, BlockingError> = { ...snap.blockingErrors }

  // Fold transform failures in as blocking errors.
  for (const [fieldKey, message] of Object.entries(inst.cellErrors)) {
    blockingErrors[fieldKey] = {
      fieldKey,
      fieldLabel: fields.find((f) => f.field_key === fieldKey)?.label?.fa ?? fieldKey,
      ruleId: null,
      message,
    }
  }

  return {
    blockingErrors,
    softWarnings: snap.softWarnings,
    hasBlocking: Object.keys(blockingErrors).length > 0,
    hasWarnings: snap.softWarnings.length > 0,
  }
}

export interface ValidationSummary {
  totalRecords: number
  totalInstances: number
  instancesWithErrors: number
  instancesWithWarnings: number
  /** True when every instance is free of blocking errors. */
  allClean: boolean
}

export function summarizeValidation(
  fields: FieldDefinition[],
  conditionalRules: ConditionalRule[],
  records: BuiltRecord[],
): ValidationSummary {
  let totalInstances = 0
  let withErrors = 0
  let withWarnings = 0
  for (const rec of records) {
    for (const inst of rec.instances) {
      totalInstances++
      const v = validateInstance(fields, conditionalRules, inst)
      if (v.hasBlocking) withErrors++
      if (v.hasWarnings) withWarnings++
    }
  }
  return {
    totalRecords: records.length,
    totalInstances,
    instancesWithErrors: withErrors,
    instancesWithWarnings: withWarnings,
    allClean: withErrors === 0,
  }
}
